<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { RotateCcw, Search, UserPlus, UserRound } from '@lucide/vue'
import {
  Badge,
  Button,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Section,
  Select,
  Spinner,
  Stat,
  Table,
  Textarea,
  Toolbar,
  useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { getUserSummary, listUsers, provisionUser } from '../api/users.js'
import { useAdminAuth } from '../composables/useAdminAuth.js'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAdminAuth()
const toast = useToast()
const isAdmin = computed(() => auth.account.value.role === 'admin')
const provisionOpen = ref(false)
const provisioning = ref(false)
const provision = reactive({
  email: '',
  password: '',
  password_confirmation: '',
  locale: 'zh',
  initial_credits: '0',
  reason: '',
})

const filters = reactive({
  q: '',
  account_type: '',
  subscription_status: '',
  limit: 50,
  since: typeof route.query.since === 'string' ? route.query.since.slice(0, 10) : dateInputDaysAgo(29),
  until: typeof route.query.until === 'string' ? route.query.until.slice(0, 10) : dateInputDaysAgo(0),
})
const summary = reactive({
  total_users: 0,
  registered_users: 0,
  guest_users: 0,
  players: 0,
  paying_users: 0,
})
const items = ref([])
const total = ref(0)
const nextCursor = ref(null)
const currentCursor = ref(null)
const cursorStack = ref([])
const loading = ref(false)
const summaryLoading = ref(false)
const error = ref('')
const summaryError = ref('')

const columns = computed(() => [
  { key: 'identity', label: t('visualGameAdmin.users.table.identity'), width: '250px' },
  { key: 'account_type', label: t('visualGameAdmin.users.table.type'), width: '100px' },
  { key: 'display_name', label: t('visualGameAdmin.users.table.displayName'), width: '150px' },
  { key: 'locale', label: t('visualGameAdmin.users.table.locale'), width: '80px' },
  { key: 'credits', label: t('visualGameAdmin.users.table.credits'), width: '110px', align: 'right', mono: true },
  { key: 'credits_spent', label: t('visualGameAdmin.users.table.creditsSpent'), width: '120px', align: 'right', mono: true },
  { key: 'credits_granted', label: t('visualGameAdmin.users.table.creditsGranted'), width: '120px', align: 'right', mono: true },
  { key: 'subscription', label: t('visualGameAdmin.users.table.subscription'), width: '150px' },
  { key: 'runs', label: t('visualGameAdmin.users.table.runs'), width: '80px', align: 'right', mono: true },
  { key: 'turns', label: t('visualGameAdmin.users.table.turns'), width: '80px', align: 'right', mono: true },
  { key: 'interactions', label: t('visualGameAdmin.users.table.interactions'), width: '90px', align: 'right', mono: true },
  { key: 'say_turns', label: t('visualGameAdmin.users.table.sayTurns'), width: '90px', align: 'right', mono: true },
  { key: 'act_turns', label: t('visualGameAdmin.users.table.actTurns'), width: '90px', align: 'right', mono: true },
  { key: 'completed_runs', label: t('visualGameAdmin.users.table.completedRuns'), width: '100px', align: 'right', mono: true },
  { key: 'completed_seasons', label: t('visualGameAdmin.users.table.completedSeasons'), width: '100px', align: 'right', mono: true },
  { key: 'endings', label: t('visualGameAdmin.users.table.endings'), width: '80px', align: 'right', mono: true },
  { key: 'orders', label: t('visualGameAdmin.users.table.orders'), width: '80px', align: 'right', mono: true },
  { key: 'last_active_at', label: t('visualGameAdmin.users.table.lastActive'), width: '180px' },
  { key: 'created_at', label: t('visualGameAdmin.users.table.createdAt'), width: '180px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '100px' },
])

const accountTypeOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.users.filters.all') },
  { value: 'registered', label: t('visualGameAdmin.users.type.registered') },
  { value: 'guest', label: t('visualGameAdmin.users.type.guest') },
])

const subscriptionOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.users.filters.all') },
  { value: 'none', label: t('visualGameAdmin.users.subscriptionStatus.none') },
  { value: 'active', label: t('visualGameAdmin.users.subscriptionStatus.active') },
  { value: 'expired', label: t('visualGameAdmin.users.subscriptionStatus.expired') },
  { value: 'canceled', label: t('visualGameAdmin.users.subscriptionStatus.canceled') },
])
const localeOptions = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
]
const provisionValid = computed(() => {
  const credits = Number(provision.initial_credits)
  return provision.email.includes('@')
    && provision.password.length >= 8
    && provision.password === provision.password_confirmation
    && Number.isInteger(credits)
    && credits >= 0
    && credits <= 10000000
    && provision.reason.trim().length >= 3
})

const pageNumber = computed(() => cursorStack.value.length + 1)
const pageCount = computed(() => Math.max(1, Math.ceil(total.value / filters.limit)))
const pageSummary = computed(() => t('visualGameAdmin.users.pagination.summary', {
  total: total.value,
  page: pageNumber.value,
  pages: pageCount.value,
}))

onMounted(async () => {
  await Promise.all([loadSummary(), loadPage(null)])
})

async function loadSummary() {
  summaryLoading.value = true
  summaryError.value = ''
  try {
    Object.assign(summary, await getUserSummary())
  } catch (requestError) {
    summaryError.value = requestError.message
  } finally {
    summaryLoading.value = false
  }
}

async function loadPage(cursor) {
  loading.value = true
  error.value = ''
  currentCursor.value = cursor
  try {
    const page = await listUsers(queryFor(cursor))
    items.value = page.items
    total.value = page.total
    nextCursor.value = page.next_cursor
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function queryFor(cursor) {
  const query = { limit: filters.limit }
  if (['all', 'sign_up', 'login'].includes(route.query.funnel_operation)) {
    query.funnel_operation = route.query.funnel_operation
    if (typeof route.query.device_types === 'string') query.device_types = route.query.device_types
  }
  if (filters.q.trim()) query.q = filters.q.trim()
  if (filters.account_type) query.account_type = filters.account_type
  if (filters.subscription_status) query.subscription_status = filters.subscription_status
  query.since = query.funnel_operation ? new Date(`${filters.since}T00:00:00.000Z`).toISOString() : startOfDate(filters.since).toISOString()
  if (query.funnel_operation) {
    const end = new Date(`${filters.until}T00:00:00.000Z`)
    end.setUTCDate(end.getUTCDate() + 1)
    query.until = end.toISOString()
  } else query.until = startOfNextDate(filters.until).toISOString()
  if (cursor !== null) query.cursor = cursor
  return query
}

async function clearFunnelFilter() {
  await router.replace({ path: '/users' })
  filters.since = dateInputDaysAgo(29)
  filters.until = dateInputDaysAgo(0)
  cursorStack.value = []
  await loadPage(null)
}

function applyFilters() {
  cursorStack.value = []
  loadPage(null)
}

function resetFilters() {
  filters.q = ''
  filters.account_type = ''
  filters.subscription_status = ''
  filters.since = dateInputDaysAgo(29)
  filters.until = dateInputDaysAgo(0)
  cursorStack.value = []
  loadPage(null)
}

function nextPage() {
  cursorStack.value = [...cursorStack.value, currentCursor.value]
  loadPage(nextCursor.value)
}

function previousPage() {
  const stack = [...cursorStack.value]
  const cursor = stack.pop()
  cursorStack.value = stack
  loadPage(cursor)
}

function changePageSize(size) {
  filters.limit = size
  cursorStack.value = []
  loadPage(null)
}

function viewUser(userId) {
  router.push({ name: 'user-detail', params: { id: userId } })
}

function openProvision() {
  provision.email = ''
  provision.password = ''
  provision.password_confirmation = ''
  provision.locale = 'zh'
  provision.initial_credits = '0'
  provision.reason = ''
  provisionOpen.value = true
}

async function submitProvision() {
  provisioning.value = true
  try {
    const result = await provisionUser({
      mutation_id: crypto.randomUUID(),
      email: provision.email,
      password: provision.password,
      locale: provision.locale,
      initial_credits: Number(provision.initial_credits),
      reason: provision.reason.trim(),
    })
    provisionOpen.value = false
    toast.success(t('visualGameAdmin.users.provision.success'))
    await router.push({ name: 'user-detail', params: { id: result.detail.user.id } })
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    provisioning.value = false
  }
}

function typeVariant(accountType) {
  switch (accountType) {
    case 'registered':
      return 'success'
    case 'guest':
      return 'info'
    default:
      throw new Error(`Unhandled account type: ${accountType}`)
  }
}

function formatIdentity(user) {
  return user.email === null ? user.id : user.email
}

function formatOptional(value) {
  return value === null ? '—' : value
}

function formatDate(value) {
  return value === null ? '—' : new Date(value).toLocaleString()
}

function dateInputDaysAgo(days) {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() - days)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfDate(value) {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) throw new Error(`Invalid analytics start date: ${value}`)
  return date
}

function startOfNextDate(value) {
  const date = startOfDate(value)
  date.setDate(date.getDate() + 1)
  return date
}
</script>

<template>
  <div class="users-page vga-page">
    <PageHeader :title="t('visualGameAdmin.users.title')">
      <template v-if="isAdmin" #actions>
        <Button @click="openProvision">
          <UserPlus :size="16" />
          {{ t('visualGameAdmin.users.provision.trigger') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="route.query.funnel_operation" class="users-page__summary-error" role="status">
      <span>{{ t(`visualGameAdmin.pageFunnel.userFilter_${route.query.funnel_operation}`) }}<template v-if="route.query.device_types"> · {{ t('visualGameAdmin.conversionFunnelDevices.label') }}: {{ String(route.query.device_types).split(',').map(device => t(`visualGameAdmin.conversionFunnelDevices.${device}`)).join(' / ') }}</template></span>
      <Button variant="ghost" size="sm" @click="clearFunnelFilter">{{ t('visualGameAdmin.common.reset') }}</Button>
    </div>
    <div class="users-page__stats" data-insp-path="apps/visual-game-admin/src/pages/Users.vue#summary">
      <Stat :label="t('visualGameAdmin.users.metrics.total')" :value="summaryLoading ? '…' : summary.total_users" />
      <Stat :label="t('visualGameAdmin.users.metrics.registered')" :value="summaryLoading ? '…' : summary.registered_users" />
      <Stat :label="t('visualGameAdmin.users.metrics.guests')" :value="summaryLoading ? '…' : summary.guest_users" />
      <Stat :label="t('visualGameAdmin.users.metrics.players')" :value="summaryLoading ? '…' : summary.players" />
      <Stat :label="t('visualGameAdmin.users.metrics.payers')" :value="summaryLoading ? '…' : summary.paying_users" />
    </div>
    <div v-if="summaryError" class="users-page__summary-error" role="alert">
      <span class="vga-error">{{ summaryError }}</span>
      <Button variant="ghost" size="sm" @click="loadSummary">
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </div>

    <Section data-insp-path="apps/visual-game-admin/src/pages/Users.vue#filters">
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.users.filters.q') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.users.filters.qPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.users.filters.type') }}</label>
          <Select v-model="filters.account_type" :options="accountTypeOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.users.filters.since') }}</label>
          <Input v-model="filters.since" type="date" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.users.filters.until') }}</label>
          <Input v-model="filters.until" type="date" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.users.filters.subscription') }}</label>
          <Select v-model="filters.subscription_status" :options="subscriptionOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" />
            {{ t('visualGameAdmin.users.filters.search') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" />
            {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <Section :padded="false" data-insp-path="apps/visual-game-admin/src/pages/Users.vue#table">
      <div v-if="loading" class="vga-state">
        <Spinner />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="loadPage(currentCursor)">
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>
      <EmptyState
        v-else-if="items.length === 0"
        :title="t('visualGameAdmin.users.empty.title')"
        :description="t('visualGameAdmin.users.empty.description')"
      >
        <template #action>
          <Button variant="secondary" @click="resetFilters">
            {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </EmptyState>
      <Table v-else :columns="columns" :rows="items">
        <template #identity="{ row }">
          <div class="users-page__identity">
            <UserRound :size="16" />
            <span>{{ formatIdentity(row) }}</span>
          </div>
        </template>
        <template #account_type="{ row }">
          <Badge :variant="typeVariant(row.account_type)" size="sm">
            {{ t(`visualGameAdmin.users.type.${row.account_type}`) }}
          </Badge>
        </template>
        <template #display_name="{ row }">{{ formatOptional(row.display_name) }}</template>
        <template #locale="{ row }">{{ formatOptional(row.locale) }}</template>
        <template #subscription="{ row }">
          <span>{{ row.subscription.level }}</span>
          <span class="vga-muted"> · {{ t(`visualGameAdmin.users.subscriptionStatus.${row.subscription.status}`) }}</span>
        </template>
        <template #credits_spent="{ row }">{{ row.interaction_stats.credits_spent }}</template>
        <template #credits_granted="{ row }">{{ row.interaction_stats.credits_granted }}</template>
        <template #runs="{ row }">{{ row.stats.runs }}</template>
        <template #turns="{ row }">{{ row.stats.turns }}</template>
        <template #interactions="{ row }">{{ row.interaction_stats.interactions }}</template>
        <template #say_turns="{ row }">{{ row.interaction_stats.say_turns }}</template>
        <template #act_turns="{ row }">{{ row.interaction_stats.act_turns }}</template>
        <template #completed_runs="{ row }">{{ row.interaction_stats.completed_runs }}</template>
        <template #completed_seasons="{ row }">{{ row.interaction_stats.completed_seasons }}</template>
        <template #endings="{ row }">{{ row.stats.endings }}</template>
        <template #orders="{ row }">{{ row.stats.orders }}</template>
        <template #last_active_at="{ row }">{{ formatDate(row.last_active_at) }}</template>
        <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
        <template #actions="{ row }">
          <Button size="sm" variant="ghost" @click="viewUser(row.id)">
            {{ t('visualGameAdmin.common.view') }}
          </Button>
        </template>
      </Table>
    </Section>

    <Pagination
      :has-prev="cursorStack.length > 0"
      :has-next="nextCursor !== null"
      :loading="loading"
      :summary="pageSummary"
      :page-size="filters.limit"
      :page-size-options="[20, 50, 100]"
      :page-size-label="t('visualGameAdmin.users.pagination.pageSize')"
      :prev-label="t('visualGameAdmin.users.pagination.previous')"
      :next-label="t('visualGameAdmin.users.pagination.next')"
      @prev="previousPage"
      @next="nextPage"
      @update:page-size="changePageSize"
    />

    <Modal v-model:open="provisionOpen" :title="t('visualGameAdmin.users.provision.title')" :close-on-backdrop="!provisioning">
      <form class="users-page__provision-form" @submit.prevent="submitProvision">
        <p class="users-page__provision-notice">{{ t('visualGameAdmin.users.provision.notice') }}</p>
        <FormField :label="t('visualGameAdmin.users.provision.email')" required>
          <Input v-model="provision.email" type="email" autocomplete="off" :maxlength="254" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.users.provision.password')" required>
          <Input v-model="provision.password" type="password" autocomplete="new-password" :minlength="8" :maxlength="256" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.users.provision.passwordConfirmation')" required>
          <Input v-model="provision.password_confirmation" type="password" autocomplete="new-password" :minlength="8" :maxlength="256" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.users.provision.locale')" required>
          <Select v-model="provision.locale" :options="localeOptions" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.users.provision.initialCredits')" required>
          <Input v-model="provision.initial_credits" type="number" min="0" max="10000000" step="1" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.users.provision.reason')" required>
          <Textarea v-model="provision.reason" :maxlength="500" :rows="3" block />
        </FormField>
      </form>
      <template #footer>
        <Button variant="ghost" :disabled="provisioning" @click="provisionOpen = false">
          {{ t('visualGameAdmin.common.cancel') }}
        </Button>
        <Button :loading="provisioning" :disabled="!provisionValid" @click="submitProvision">
          {{ t('visualGameAdmin.users.provision.confirm') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.users-page__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--drama-space-md);
}

.users-page__summary-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.users-page__identity {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  min-width: 0;
}

.users-page__identity span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.users-page__provision-form {
  display: grid;
  gap: var(--drama-space-lg);
}

.users-page__provision-notice {
  margin: 0;
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

</style>
